import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Badge, type BadgeTone } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import type { TipoAusencia, TipoAusenciaCategoria } from './types.js';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

const CATEGORIA_INFO: Record<TipoAusenciaCategoria, { label: string; tone: BadgeTone }> = {
  antiguedad:          { label: 'Antigüedad',         tone: 'info' },
  cupon_fijo:          { label: 'Cupón fijo',          tone: 'neutral' },
  permisos_especiales: { label: 'Permisos especiales', tone: 'orange' },
};

// Prompt AD — Pantalla 1: tabla plana (Nombre/Categoría/Categoría visible/
// Rango de años/Días otorgados/Activo), mismo patrón de navegación de dos
// niveles ya validado en Días Festivos (AC-2). Muestra activos e inactivos —
// el estado es una columna, no un filtro (RH necesita ver qué desactivó).
export function TiposAusenciaPage() {
  const { get, patch } = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  const { data: tipos, isLoading } = useQuery({
    queryKey: ['tipos-ausencia'],
    queryFn: () => get<TipoAusencia[]>('/tipos-ausencia'),
  });

  // Prompt BB-1 — reemplaza el ícono de basura por un switch bidireccional
  // (mismo patrón que UsuariosPage.tsx/DiasFestivosPage.tsx): PATCH ya
  // permitía reactivar (activo:true) desde AD-2, esto solo le da control
  // visual de un solo clic en vez de tener que abrir el detalle.
  const toggleMutation = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) => patch(`/tipos-ausencia/${id}`, { activo }),
    onSuccess: (_data, { activo }) => {
      queryClient.invalidateQueries({ queryKey: ['tipos-ausencia'] });
      showToast('success', activo ? 'Tipo de ausencia reactivado' : 'Tipo de ausencia desactivado');
    },
    onError: err => showToast('error', errorMessage(err, 'No fue posible actualizar el estado del tipo de ausencia')),
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Tipos de Ausencia</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>Catálogo de antigüedad (LFT) y cupones fijos de RH</div>
        </div>
        <Button onClick={() => navigate('/tipos-ausencia/nuevo')}>+ Nuevo Tipo</Button>
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Categoría</th>
                <th>Categoría visible</th>
                <th>Rango de años</th>
                <th>Días otorgados</th>
                <th>Días otorgados nómina</th>
                <th style={{ textAlign: 'center' }}>Activo</th>
              </tr>
            </thead>
            <tbody>
              {!tipos || tipos.length === 0 ? (
                <tr>
                  <td className="empty-td" colSpan={7}>
                    {isLoading ? 'Cargando…' : 'No hay tipos de ausencia todavía'}
                  </td>
                </tr>
              ) : (
                tipos.map(t => (
                  <tr key={t.id}>
                    <td>
                      <span className="tk-folio" onClick={() => navigate(`/tipos-ausencia/${t.id}`)}>
                        {t.nombre}
                      </span>
                    </td>
                    <td>
                      <Badge tone={CATEGORIA_INFO[t.categoria].tone}>{CATEGORIA_INFO[t.categoria].label}</Badge>
                    </td>
                    <td style={{ color: 'var(--muted)' }}>{t.categoria_visible}</td>
                    <td style={{ color: 'var(--muted)' }}>
                      {t.categoria === 'antiguedad' ? `${t.anio_desde}${t.anio_hasta ? `–${t.anio_hasta}` : ''}` : '—'}
                    </td>
                    <td>{t.dias_otorgados}</td>
                    <td style={{ color: t.dias_otorgados_nomina === null ? 'var(--muted)' : undefined }}>
                      {t.categoria === 'antiguedad' ? (t.dias_otorgados_nomina ?? '—') : '—'}
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center' }}>
                      <label className="tog" title={t.activo ? 'Desactivar tipo de ausencia' : 'Reactivar tipo de ausencia'}>
                        <input
                          type="checkbox"
                          checked={t.activo}
                          onChange={e => toggleMutation.mutate({ id: t.id, activo: e.target.checked })}
                        />
                        <span className="tog-slider" />
                      </label>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
